import { Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle } from "lucide-react";

import { SectionHeading } from "./SectionHeading";
import { CtaBand } from "./CtaBand";
import { QuickQuote } from "./QuickQuote";
import { SITE_URL, ogImageDefault, ogImageMeta } from "../lib/seo";
import { kostenDisclaimer } from "../lib/kosten-content";
import type { KostenGuide } from "../lib/kosten-guides";

interface KostenPageProps {
  guide: KostenGuide;
}

export function KostenPage({ guide }: KostenPageProps) {
  const path = `/${guide.slug}-kosten`;

  return (
    <>
      <div className="py-14 sm:py-20 lg:py-28">
        <div className="container-tight">
          <Link
            to="/diensten/$slug"
            params={{ slug: guide.slug }}
            className="mx-auto flex w-fit items-center gap-1 text-sm font-semibold text-primary hover:text-primary/80"
          >
            <ArrowRight className="h-4 w-4 rotate-180" aria-hidden="true" />
            Terug naar {guide.slug === "dakkapel" ? "dakkapel" : guide.slug}
          </Link>

          <SectionHeading
            as="h1"
            eyebrow="Kostengids"
            title={guide.title}
            description={guide.description}
            align="center"
            className="mx-auto"
          />

          <div className="mx-auto mt-10 max-w-3xl space-y-14 sm:mt-14">
            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Kort antwoord: de gemiddelde kosten
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {guide.shortAnswer}
              </p>
            </section>

            <section className="rounded-2xl border border-border/60 bg-card p-6 sm:p-8">
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                {guide.priceLabel}
              </h2>
              <p className="mt-3 font-heading text-3xl font-bold leading-tight tracking-tight text-primary sm:text-4xl">
                {guide.price}
              </p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {guide.priceNoot}
              </p>

              <h3 className="mt-6 font-heading text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Prijs hangt af van
              </h3>
              <ul className="mt-3 space-y-2.5 text-sm leading-relaxed text-foreground/80">
                {guide.factoren.map((factor) => (
                  <li key={factor} className="flex items-start gap-2.5">
                    <CheckCircle
                      className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                      aria-hidden="true"
                    />
                    <span className="min-w-0">{factor}</span>
                  </li>
                ))}
              </ul>

              <p className="mt-5 text-xs leading-relaxed text-muted-foreground">
                {kostenDisclaimer}
              </p>

              <div className="mt-6">
                <Link
                  to="/contact"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Vraag een prijs op maat aan
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </div>
            </section>

            {guide.tabel && guide.tabel.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                  {guide.tabelTitel}
                </h2>
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full min-w-[34rem] border-collapse text-left text-sm">
                    <thead>
                      <tr className="border-b border-border/60">
                        <th className="py-3 pr-4 font-heading font-semibold text-foreground">
                          Variant
                        </th>
                        <th className="py-3 pr-4 font-heading font-semibold text-foreground">
                          Richtprijs
                        </th>
                        <th className="py-3 font-heading font-semibold text-foreground">
                          Toelichting
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {guide.tabel.map((row) => (
                        <tr key={row.label} className="border-b border-border/40 align-top">
                          <td className="py-3 pr-4 font-medium text-foreground">{row.label}</td>
                          <td className="py-3 pr-4 font-semibold text-primary">{row.prijs}</td>
                          <td className="py-3 text-muted-foreground">{row.noot}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {guide.lokaal && guide.lokaal.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                  {guide.lokaalTitel}
                </h2>
                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  {guide.lokaal.map((item) => (
                    <div
                      key={item.plaats}
                      className="rounded-xl border border-border/60 bg-card p-4 sm:p-5"
                    >
                      <h3 className="font-heading text-base font-semibold text-foreground">
                        {item.plaats}
                      </h3>
                      <p className="mt-1 font-heading text-lg font-bold text-primary">
                        {item.prijs}
                      </p>
                      <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                        {item.noot}
                      </p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-sm text-muted-foreground">
                  Werkgebied: Dordrecht, Zwijndrecht, Papendrecht, Sliedrecht en
                  Hendrik-Ido-Ambacht. Binnen de Drechtsteden rekenen we geen voorrijkosten.
                </p>
                <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm font-semibold">
                  <Link
                    to="/timmerman-dordrecht"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                  >
                    Timmerman Dordrecht
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <Link
                    to="/timmerman-zwijndrecht"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                  >
                    Timmerman Zwijndrecht
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                  <Link
                    to="/kosten"
                    className="inline-flex items-center gap-1 text-primary hover:text-primary/80"
                  >
                    Alle prijzen en kosten
                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </Link>
                </div>
              </section>
            )}

            {guide.tips && guide.tips.length > 0 && (
              <section>
                <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                  Tips om slim te besparen op {guide.slug}-kosten
                </h2>
                <ul className="mt-4 space-y-3">
                  {guide.tips.map((tip) => (
                    <li
                      key={tip}
                      className="flex gap-3 text-sm leading-relaxed text-muted-foreground"
                    >
                      <span
                        aria-hidden="true"
                        className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary"
                      />
                      <span>{tip}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section>
              <h2 className="font-heading text-2xl font-bold tracking-tight text-foreground">
                Veelgestelde vragen over {guide.slug}-kosten
              </h2>
              <div className="mt-6 space-y-4">
                {guide.faq.map((item) => (
                  <div
                    key={item.vraag}
                    className="rounded-xl border border-border/60 bg-card p-4 sm:p-5"
                  >
                    <h3 className="font-heading text-base font-semibold text-foreground">
                      {item.vraag}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                      {item.antwoord}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            <QuickQuote onderwerp={guide.slug} />
          </div>
        </div>
      </div>

      <CtaBand />
    </>
  );
}

export function kostenHead(guide: KostenGuide) {
  const path = `/${guide.slug}-kosten`;
  return {
    meta: [
      { title: guide.title },
      { name: "description", content: guide.description },
      { property: "og:title", content: guide.socialTitle },
      { property: "og:description", content: guide.socialDescription },
      { property: "og:type", content: "article" },
      { property: "og:url", content: `${SITE_URL}${path}` },
      ...ogImageMeta(ogImageDefault, `${guide.title} — Fedor Timmerwerken`),
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}${path}` }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: guide.title,
          description: guide.description,
          inLanguage: "nl-NL",
          image: ogImageDefault,
          author: { "@type": "Organization", name: "Fedor Timmerwerken" },
          publisher: {
            "@type": "Organization",
            name: "Fedor Timmerwerken",
            logo: { "@type": "ImageObject", url: ogImageDefault },
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": `${SITE_URL}${path}` },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: guide.faq.map((item) => ({
            "@type": "Question",
            name: item.vraag,
            acceptedAnswer: { "@type": "Answer", text: item.antwoord },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Diensten", item: `${SITE_URL}/diensten` },
            {
              "@type": "ListItem",
              position: 3,
              name: `${guide.title.split(":")[0]}`,
              item: `${SITE_URL}${path}`,
            },
          ],
        }),
      },
    ],
  };
}
